import isEmpty from 'lodash/isEmpty';
import { DateTime } from 'luxon';
import { getContentClassByContentType, SearchFilterContentTypes } from 'search-gateway-client/components';
import { ContentCollection } from 'stockblocks-client';

import { TypeAheadSuggestedPage } from '../NavSearchElements/NavSearchTypes';
import { NullSuggestions } from './NullSearchDropdown';
import { WithinAssetContentTypeSuggestion } from './WithinAssetSuggestion';
import {
    KeyboardDirectionsType,
    keyboardEventsKey,
    ListDirections,
    ListDirectionsType,
    NullStateSuggestionTypes,
    suggestionTypes,
    suggestionTypesKey,
} from './constants';

export type ComputedSuggestion = {
    keywords: string[];
    relatedCollections: ContentCollection[];
    withinAssetContentTypeSuggestions: WithinAssetContentTypeSuggestion[];
    relatedContentTypeSuggestion: WithinAssetContentTypeSuggestion[];
    suggestedPages: TypeAheadSuggestedPage[];
    adobePluginLink: string[];
};
export enum interactiveElements {
    SearchBar = 'SEARCH_BAR',
    SeeMoreCollectionLink = 'SEE_MORE_COLLECTION_LINK',
}
export type Sections = suggestionTypes | interactiveElements;
export type NullStateSections = NullStateSuggestionTypes | interactiveElements;
export type Node = Record<KeyboardDirectionsType, Sections[] | NullStateSections[] | null> & {
    DIRECTION?: ListDirectionsType;
    // A link that only renders alongside this suggestion list, so it is skipped while the list is empty.
    SHOWN_WITH?: Sections | NullStateSections;
};
export type DirectedGraphType = Record<Sections, Node>;
export type NullStateDirectedGraphType = Record<NullStateSections, Node>;

export const findTheNextNodeAndIndex = (
    directedGraph: DirectedGraphType | NullStateDirectedGraphType,
    direction: KeyboardDirectionsType,
    origin: Sections | NullStateSections,
    suggestions: ComputedSuggestion | NullSuggestions,
    selectedIndex: number,
): [Sections | NullStateSections, number] => {
    // traverse the graph using breath first search, and find the first available node
    // available node being the first non empty suggestion
    let curr: Sections | NullStateSections = origin;
    const visited = { [origin]: true };
    const queue: ListDirectionsType[] = [];
    const nextNodes = (directedGraph[curr as keyof typeof directedGraph][direction]
        ? directedGraph[curr as keyof typeof directedGraph][direction]!
        : []) as unknown as ListDirectionsType[];
    queue.push(...nextNodes);
    while (!isEmpty(queue)) {
        curr = queue.shift() as Sections | NullStateSections;
        if (curr != null && !visited[curr]) {
            if (!(curr in suggestions)) {
                const shownWith = directedGraph[curr as keyof typeof directedGraph].SHOWN_WITH;
                if (!shownWith || !isEmpty(suggestions[shownWith as keyof typeof suggestions])) {
                    return [curr, -1];
                }
            } else if (!isEmpty(suggestions[curr as keyof typeof suggestions])) {
                const listDirection = directedGraph[curr as keyof typeof directedGraph].DIRECTION;
                const newNodeAndKeyboardDirectionIsTheSame =
                    (direction === keyboardEventsKey.ARROW_UP && listDirection === ListDirections.VERTICAL) ||
                    (direction === keyboardEventsKey.ARROW_LEFT && listDirection === ListDirections.HORIZONTAL);
                const newIndex = newNodeAndKeyboardDirectionIsTheSame
                    ? (suggestions[curr as keyof typeof suggestions] as unknown[]).length - 1
                    : 0;
                return [curr, newIndex];
            }
            queue.push(
                ...((directedGraph[curr as keyof typeof directedGraph][direction]
                    ? directedGraph[curr as keyof typeof directedGraph][direction]!
                    : []) as unknown as ListDirectionsType[]),
            );
            visited[curr] = true;
        }
    }

    if (!(origin in suggestions)) {
        return [origin, selectedIndex];
    }

    // handle what the next index will be based on the direction of the current list
    // and the next list
    const originListDirection = directedGraph[origin as keyof typeof directedGraph].DIRECTION;
    const originListIsVerticalButKeyboardDirectionIsHorizontal =
        originListDirection === ListDirections.VERTICAL &&
        (direction === keyboardEventsKey.ARROW_LEFT || direction === keyboardEventsKey.ARROW_RIGHT);
    const originListIsHorizontalButKeyboardDirectionIsVertical =
        originListDirection === ListDirections.HORIZONTAL &&
        (direction === keyboardEventsKey.ARROW_UP || direction === keyboardEventsKey.ARROW_DOWN);
    if (originListIsVerticalButKeyboardDirectionIsHorizontal || originListIsHorizontalButKeyboardDirectionIsVertical) {
        return [origin, selectedIndex];
    }

    return [
        origin,
        selectedIndex === 0 ? (suggestions[origin as keyof typeof suggestions] as unknown[]).length - 1 : 0,
    ];
};

export const calculateNewIndexBasedonDirection = (selectedIndex: number, direction: KeyboardDirectionsType): number => {
    if (direction === keyboardEventsKey.ARROW_UP || direction === keyboardEventsKey.ARROW_LEFT) {
        return selectedIndex - 1;
    }
    return selectedIndex + 1;
};

export const UILayoutGraph: DirectedGraphType = {
    [interactiveElements.SearchBar]: {
        [keyboardEventsKey.ARROW_UP]: null,
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
    },
    [suggestionTypesKey.KEYWORDS]: {
        [keyboardEventsKey.ARROW_UP]: null,
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.SUGGESTED_PAGES, suggestionTypesKey.ADOBE_PLUGIN],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: [
            suggestionTypesKey.WITHIN_ASSET,
            suggestionTypesKey.RELATED_CONTENT_TYPE,
            suggestionTypesKey.RELATED_COLLECTION,
        ],
        DIRECTION: ListDirections.VERTICAL,
    },
    [suggestionTypesKey.WITHIN_ASSET]: {
        [keyboardEventsKey.ARROW_UP]: null,
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.RELATED_CONTENT_TYPE],
        [keyboardEventsKey.ARROW_LEFT]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.VERTICAL,
    },
    [suggestionTypesKey.RELATED_CONTENT_TYPE]: {
        [keyboardEventsKey.ARROW_UP]: [suggestionTypesKey.WITHIN_ASSET],
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.RELATED_COLLECTION],
        [keyboardEventsKey.ARROW_LEFT]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.VERTICAL,
    },
    [suggestionTypesKey.RELATED_COLLECTION]: {
        [keyboardEventsKey.ARROW_UP]: [suggestionTypesKey.RELATED_CONTENT_TYPE],
        [keyboardEventsKey.ARROW_DOWN]: [interactiveElements.SeeMoreCollectionLink],
        [keyboardEventsKey.ARROW_LEFT]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.HORIZONTAL,
    },
    [suggestionTypesKey.SUGGESTED_PAGES]: {
        [keyboardEventsKey.ARROW_UP]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.ADOBE_PLUGIN],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: [
            suggestionTypesKey.WITHIN_ASSET,
            suggestionTypesKey.RELATED_CONTENT_TYPE,
            suggestionTypesKey.RELATED_COLLECTION,
        ],
        DIRECTION: ListDirections.HORIZONTAL,
    },
    [interactiveElements.SeeMoreCollectionLink]: {
        [keyboardEventsKey.ARROW_UP]: [suggestionTypesKey.RELATED_COLLECTION],
        [keyboardEventsKey.ARROW_DOWN]: [suggestionTypesKey.ADOBE_PLUGIN],
        [keyboardEventsKey.ARROW_LEFT]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_RIGHT]: null,
        SHOWN_WITH: suggestionTypesKey.RELATED_COLLECTION,
    },
    [suggestionTypesKey.ADOBE_PLUGIN]: {
        [keyboardEventsKey.ARROW_UP]: [suggestionTypesKey.KEYWORDS],
        [keyboardEventsKey.ARROW_DOWN]: null,
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.VERTICAL,
    },
};

export const NullStateUIGraph: NullStateDirectedGraphType = {
    [interactiveElements.SearchBar]: {
        [keyboardEventsKey.ARROW_UP]: null,
        [keyboardEventsKey.ARROW_DOWN]: [NullStateSuggestionTypes.latestCollections],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
    },
    [NullStateSuggestionTypes.latestCollections]: {
        [keyboardEventsKey.ARROW_UP]: null,
        [keyboardEventsKey.ARROW_DOWN]: [NullStateSuggestionTypes.trendingCollections],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.HORIZONTAL,
    },
    [NullStateSuggestionTypes.trendingCollections]: {
        [keyboardEventsKey.ARROW_UP]: [NullStateSuggestionTypes.latestCollections],
        [keyboardEventsKey.ARROW_DOWN]: [interactiveElements.SeeMoreCollectionLink],
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
        DIRECTION: ListDirections.HORIZONTAL,
    },
    [interactiveElements.SeeMoreCollectionLink]: {
        [keyboardEventsKey.ARROW_UP]: [NullStateSuggestionTypes.trendingCollections],
        [keyboardEventsKey.ARROW_DOWN]: null,
        [keyboardEventsKey.ARROW_LEFT]: null,
        [keyboardEventsKey.ARROW_RIGHT]: null,
        SHOWN_WITH: NullStateSuggestionTypes.trendingCollections,
    },
};

export interface SearchEntry {
    term: string;
    date: string;
}

export type SearchHistoryType = {
    [key: string]: SearchEntry[];
};

const MAX_TERM_LENGTH = 500;
const MAX_HISTORY_ENTRIES = 5;
const HISTORY_EXPIRATION_DAYS = 60;

export function saveSearchHistory(contentType: SearchFilterContentTypes, searchTerm: string): void {
    if (!searchTerm || searchTerm.trim() === '' || typeof window === 'undefined') {
        return;
    }

    const trimmedTerm = searchTerm.length > MAX_TERM_LENGTH ? searchTerm.slice(0, MAX_TERM_LENGTH) : searchTerm;
    const contentClass = getContentClassByContentType(contentType);

    const currentDate = DateTime.now();
    const currentDateFormatted = currentDate.toFormat('yyyy-MM-dd');

    const searchHistory = localStorage.getItem('searchHistory');

    if (searchHistory) {
        try {
            const parsedHistory = JSON.parse(searchHistory) as SearchHistoryType;
            const searchEntries: SearchEntry[] = parsedHistory[contentClass] || [];
            searchEntries.push({ term: trimmedTerm, date: currentDateFormatted });

            const recentSearches = removeExpiredEntries(searchEntries, currentDate);
            const uniqueSearches = removeDuplicateEntries(recentSearches);

            parsedHistory[contentClass] = uniqueSearches.slice(-MAX_HISTORY_ENTRIES);
            localStorage.setItem('searchHistory', JSON.stringify(parsedHistory));
        } catch (e) {
            console.error('Invalid search history format:', e);
        }
    } else {
        const initialHistory: SearchHistoryType = {
            [contentClass]: [{ term: trimmedTerm, date: currentDateFormatted }],
        };

        localStorage.setItem('searchHistory', JSON.stringify(initialHistory));
    }
}

export function removeExpiredEntries(history: SearchEntry[], currentDate: DateTime): SearchEntry[] {
    const expirationDate = currentDate.minus({ days: HISTORY_EXPIRATION_DAYS });
    return history.filter((entry) => DateTime.fromISO(entry.date) >= expirationDate);
}

export function removeDuplicateEntries(history: SearchEntry[]): SearchEntry[] {
    return history.filter((term, index, self) => index === self.findIndex((t) => t.term === term.term));
}
